/**
 * Pure route identifiers and labels for the filing feature.
 *
 * These live in their own module (separate from {@link filing.routes.ts}, which holds the route
 * table and its provider graph) so that services pulled into that provider graph — e.g. the shell
 * flow services and flow strategies — can reference these enums without importing the route table.
 * Importing the route table from those services creates a barrel-mediated circular dependency that
 * surfaces at module init as `Cannot read properties of undefined (reading 'FilingFlowNavigatorService')`.
 */

/**
 * Main filing routes enum
 */
export enum FILING_ROUTES {
  NEW_CASE = 'new-case',
  EXISTING_CASE = 'existing-case',
  MULTI_CASE = 'multi-case',
  SAVED_TRANSACTIONS = 'saved-transactions',
}

/**
 * Shared child routes for all filing types (new-case, existing-case, multi-case)
 */
export enum FILING_CHILD_ROUTES {
  CASE_LIST = 'case-list',
  CASE_DETAILS = 'case-details',
  PARTIES = 'parties',
  PARTY_SEARCH = 'party-search',
  DOCUMENTS = 'documents',
  REVIEW_SUBMIT = 'review-submit',
  SENDING_PARTIES = 'sending-parties', // SubF Only
  SERVICE_LIST = 'service-list', // SubF and Multi-Case
  MANAGE_ALIASES = 'manage-aliases',
  ATTORNEY_SEARCH = 'attorney-search',
  ADD_PARTY = 'add-party',
  ADD_REPRESENTATION = 'add-representation',
  ADD_RECIPIENT = 'add-recipient',
  ADD_SERVICE_PARTY = 'add-service-party', // SubF Only
  CHECK_ADDRESS = 'check-address',
}

export enum FILING_DOCUMENT_SIDEPANEL_ROUTES {
  DOCUMENT_LINK = 'link',
  DOCUMENT_COURTESY_COPY = 'courtesy-copy',
  DOCUMENT_TEMPLATE_FORM = 'template-form',
}

/**
 * Filing router outlets enum
 */
export enum FILING_ROUTER_OUTLETS {
  SIDEPANEL_DOCUMENT_OUTLET = 'filing-documents-outlet',
}

/**
 * Route data key a filing step sets to `true` to be given exactly the height between the shell's
 * tabs row and its buttons bar, rather than growing the page. The step then scrolls inside itself
 * and the page keeps no scrollbar of its own (OPF Parties, #172670). Steps without it grow as the
 * page's content, as before.
 */
export const FILING_ROUTE_DATA_FILLS_PANEL = 'fillsFilingPanel';

/**
 * Tab/breadcrumb labels for filing child routes.
 * These constants ensure consistency between tabText and breadcrumb values.
 */
export const FILING_TAB_LABELS: Record<FILING_CHILD_ROUTES, string> = {
  [FILING_CHILD_ROUTES.CASE_LIST]: 'Case List',
  [FILING_CHILD_ROUTES.CASE_DETAILS]: 'Case Details',
  [FILING_CHILD_ROUTES.PARTIES]: 'Parties',
  [FILING_CHILD_ROUTES.PARTY_SEARCH]: 'Party Search',
  [FILING_CHILD_ROUTES.DOCUMENTS]: 'Documents',
  [FILING_CHILD_ROUTES.REVIEW_SUBMIT]: 'Review and Submit',
  [FILING_CHILD_ROUTES.SENDING_PARTIES]: 'Sending Parties',
  [FILING_CHILD_ROUTES.SERVICE_LIST]: 'Service List',
  [FILING_CHILD_ROUTES.MANAGE_ALIASES]: 'Manage Aliases',
  [FILING_CHILD_ROUTES.ATTORNEY_SEARCH]: 'Attorney Search',
  [FILING_CHILD_ROUTES.ADD_PARTY]: 'Add Party',
  [FILING_CHILD_ROUTES.ADD_REPRESENTATION]: 'Add Representation',
  [FILING_CHILD_ROUTES.ADD_RECIPIENT]: 'Add Recipient',
  [FILING_CHILD_ROUTES.ADD_SERVICE_PARTY]: 'Add Party to Serve',
  [FILING_CHILD_ROUTES.CHECK_ADDRESS]: 'Check Address',
};
